import { useEffect, useRef, useState } from 'react'
import {
  CHART_DEFAULT_OPTIONS,
  LABEL_FONT_SIZE,
  SUBTITLE_FONT_SIZE,
  TITLE_FONT_SIZE,
  UnitConversions,
  UserUnitSettings,
} from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { ChartSubHeader } from '../ChartSubHeader'
import { ChartLoadingPlaceholder } from './ChartLoadingPlaceholder'
import { CollapsibleSection } from '../CollapsibleSection'
import { formatNumber, roundToDecimal } from '../../common/utils'
import { convertBaseToDisplay } from '../../common/conversions'
import { DeviceInfo, SensorProduct, UnitCategoryId } from '../../common/enums'
import { getUserUnitSetting } from '../../utils/unitSettings'
import CanvasJSReact from '@canvasjs/react-charts'
import { Viewport } from '../../common/types'
import { createAxisLabelFormatter, alertMarkersToStripLines, AlertMarker, getHiddenRangeStripLines } from '../../utils/chartHelpers'
const { CanvasJSChart } = CanvasJSReact

interface RainfallRecord {
  timestamp: number
  value: number
}

interface DataPoints {
  rainfall: any[]
  rainfallTotals: any[]
  rainfallRate: any[]
}

interface Props {
  device: DeviceInfo
  sensorPort: string
  sensorProduct: SensorProduct
  sensorReadings: any
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions
  isLoading: boolean

  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
  showHiddenData?: boolean
  alertMarkers?: AlertMarker[]
}

export function RainfallChart({
  device,
  sensorProduct,
  sensorPort,
  sensorReadings,
  viewport,
  userUnitSettings,
  unitConversions,
  isLoading,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = () => {},
  showHiddenData = false,
  alertMarkers,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const xRef = useRef(0)
  const [showAmounts, setShowAmounts] = useState(true)
  const [showRate, setShowRate] = useState(true)
  const [showTotals, setShowTotals] = useState(true)
  const [minX, setMinX] = useState(0)
  const [maxX, setMaxX] = useState(0)


  // Check if we have valid readings data - must be before useEffects
  const hasReadings = sensorReadings?.readings && Object.keys(sensorReadings.readings).length > 0

  useEffect(() => {
    // Get hidden map from sensor data for client-side filtering
    const hiddenMap = sensorReadings?.hidden || {}
    const timestamps = sensorReadings?.readings
      ? Object.keys(sensorReadings.readings).filter(key => !hiddenMap[key])
      : []
    const minTimestamp = timestamps.length > 0 ? parseInt(timestamps[0]) * 1000 : viewport.minimum
    const maxTimestamp = timestamps.length > 0 ? parseInt(timestamps[timestamps.length - 1]) * 1000 : viewport.maximum
    const minX = viewport.minimum > minTimestamp ? viewport.minimum : minTimestamp
    const maxX = viewport.maximum < maxTimestamp ? viewport.maximum : maxTimestamp

    setMinX(minX)
    setMaxX(maxX)
  }, [viewport, sensorReadings])

  // Register click listener ONCE when chart mounts (not on every hover)
  // Uses retry mechanism since CanvasJS renders the canvas asynchronously
  // NOTE: CanvasJS creates multiple canvas elements - we need the LAST one (topmost layer)
  useEffect(() => {
    if (!containerRef.current || !hasReadings) return

    let canvas: HTMLCanvasElement | null = null
    let retryCount = 0
    const maxRetries = 20
    let timeoutId: ReturnType<typeof setTimeout>

    function onClickHandler() {
      addTimestamp(xRef.current)
    }

    function tryAttachListener() {
      const canvases = containerRef.current?.querySelectorAll('.canvasjs-chart-canvas')
      canvas = canvases && canvases.length > 0 ? (canvases[canvases.length - 1] as HTMLCanvasElement) : null
      if (canvas) {
        canvas.addEventListener('click', onClickHandler)
      } else if (retryCount < maxRetries) {
        retryCount++
        timeoutId = setTimeout(tryAttachListener, 100)
      }
    }

    tryAttachListener()

    return () => {
      clearTimeout(timeoutId)
      if (canvas) {
        canvas.removeEventListener('click', onClickHandler)
      }
    }
  }, [hasReadings, addTimestamp])

  // Guard against undefined/null device during logout or data clearing
  if (!device) {
    return null
  }

  const rainfallUserSettings = getUserUnitSetting(UnitCategoryId.Rainfall, userUnitSettings, unitConversions)
  const rainfallRateUserSettings = getUserUnitSetting(UnitCategoryId.RainfallRate, userUnitSettings, unitConversions)

  // Guard against missing unit conversions
  const rainfallConversions = unitConversions[UnitCategoryId.Rainfall]
  const rainfallRateConversions = unitConversions[UnitCategoryId.RainfallRate]

  if (!rainfallConversions || !rainfallRateConversions) {
    return (
      <CollapsibleSection title="Rainfall">
        <div className="flex items-center justify-center py-8">
          <div className="bg-yellow-500 text-white font-bold rounded px-6 py-3 text-center">
            Loading unit settings...
          </div>
        </div>
      </CollapsibleSection>
    )
  }

  const settings = {
    rainfall: {
      userSettings: rainfallUserSettings,
      conversions: rainfallConversions,
    },
    rainfallRate: {
      userSettings: rainfallRateUserSettings,
      conversions: rainfallRateConversions,
    },
  }

  const units = {
    rainfall: settings.rainfall.conversions?.abbreviations?.[rainfallUserSettings.unitId] ?? 'mm',
    rainfallRate: settings.rainfallRate.conversions?.abbreviations?.[rainfallRateUserSettings.unitId] ?? 'mm/h',
  }

  const formulas = {
    rainfall: settings.rainfall.conversions?.formulas?.[rainfallUserSettings.unitId] ?? '(x) => x',
    rainfallRate: settings.rainfallRate.conversions?.formulas?.[rainfallRateUserSettings.unitId] ?? '(x) => x',
  }

  const precision = {
    rainfall: rainfallUserSettings.precision,
    rainfallRate: rainfallRateUserSettings.precision,
  }

  let maxRainfall = 0
  let maxRainfallTotal = 0
  let maxRainfallRate = 0
  let maxYAxis = 0.1
  let total = 0

  let rainfallHourlyWindow: RainfallRecord[] = []

  const dataPoints = generateDataPoints()

  function generateDataPoints() {
    const dataPoints: DataPoints = {
      rainfall: [],
      rainfallTotals: [],
      rainfallRate: [],
    }

    if (!sensorReadings?.readings) {
      return dataPoints
    }

    // Get hidden map from sensor data for client-side filtering
    const hiddenMap = sensorReadings?.hidden || {}

    Object.keys(sensorReadings.readings)
      .filter(key => !hiddenMap[key])  // Filter out hidden timestamps
      .forEach((key: string) => {
      const ts = Number(key)
      const readingInMeters = Number(sensorReadings.readings[ts])

      const volume = convertBaseToDisplay(readingInMeters, formulas.rainfall)
      total += volume

      // get rainfall rate (base units = mm/minute)
      rainfallHourlyWindow.push({ timestamp: ts, value: readingInMeters })
      rainfallHourlyWindow = rainfallHourlyWindow.filter((record) => record.timestamp > ts - 3600)
      const ratePerHour = rainfallHourlyWindow.reduce((acc, record) => {
        return acc + record.value
      }, 0)

      const rateInMillimetersPerHour = ratePerHour * 1000
      const rateInMillimetersPerMinute = rateInMillimetersPerHour / 60
      const rate = convertBaseToDisplay(rateInMillimetersPerMinute, formulas.rainfallRate)

      if (total > maxRainfallTotal) maxRainfallTotal = total
      if (volume > maxRainfall) maxRainfall = volume
      if (rate > maxRainfallRate) maxRainfallRate = rate

      const timestamp = convertTimestamp(ts)

      dataPoints.rainfall.push({
        label: timestamp,
        x: ts * 1000,
        y: volume,
      })

      dataPoints.rainfallTotals.push({
        label: timestamp,
        x: ts * 1000,
        y: total,
      })

      dataPoints.rainfallRate.push({
        label: timestamp,
        x: ts * 1000,
        y: rate,
      })
    })

    maxYAxis = Math.max(showAmounts ? maxRainfall : 0, showTotals ? maxRainfallTotal : 0, 0.01)
    maxRainfallRate = Math.max(maxRainfallRate, 0.01)
    return dataPoints
  }

  // Build full (unfiltered) data points for ghost overlay — shows what the chart would look like without hiding
  const ghostDataPoints = (() => {
    if (!showHiddenData || !sensorReadings?.readings) {
      return { rainfall: [], rainfallTotals: [], rainfallRate: [] }
    }
    const result: DataPoints = { rainfall: [], rainfallTotals: [], rainfallRate: [] }
    let ghostTotal = 0
    let ghostWindow: RainfallRecord[] = []

    Object.keys(sensorReadings.readings)
      .forEach((key: string) => {
        const ts = Number(key)
        const readingInMeters = Number(sensorReadings.readings[ts])
        const volume = convertBaseToDisplay(readingInMeters, formulas.rainfall)
        ghostTotal += volume

        ghostWindow.push({ timestamp: ts, value: readingInMeters })
        ghostWindow = ghostWindow.filter((record) => record.timestamp > ts - 3600)
        const ratePerHour = ghostWindow.reduce((acc, record) => acc + record.value, 0)
        const rateInMillimetersPerHour = ratePerHour * 1000
        const rateInMillimetersPerMinute = rateInMillimetersPerHour / 60
        const rate = convertBaseToDisplay(rateInMillimetersPerMinute, formulas.rainfallRate)

        const timestamp = convertTimestamp(ts)
        result.rainfall.push({ label: timestamp, x: ts * 1000, y: volume })
        result.rainfallTotals.push({ label: timestamp, x: ts * 1000, y: ghostTotal })
        result.rainfallRate.push({ label: timestamp, x: ts * 1000, y: rate })
      })
    return result
  })()

  const hiddenStripLines = showHiddenData ? getHiddenRangeStripLines(sensorReadings?.hidden || {}) : []

  const rainfall = {
    name: 'Amount',
    axisYType: 'primary',
    type: 'stepArea',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'blue',
    fillOpacity: 0.4,
    dataPoints: showAmounts ? dataPoints.rainfall : [{ x: 0, y: 0 }],
  }

  const rainfallTotals = {
    name: 'Total',
    axisYType: 'primary',
    type: 'area',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'lightgreen',
    fillOpacity: 0.4,
    dataPoints: showTotals ? dataPoints.rainfallTotals : [{ x: 0, y: 0 }],
  }

  const rainfallRate = {
    name: 'Rate',
    axisYType: 'secondary',
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'red',
    fillOpacity: 0.1,
    dataPoints: showRate ? dataPoints.rainfallRate : [{ x: 0, y: 0 }],
  }

  const title =
    {
      [SensorProduct.Rainfall]: 'Rainfall',
      [SensorProduct.OpticalRainSensorSAB]: 'Rainfall (ORS)',
      [SensorProduct.OpticalRainSensorPower]: 'Rainfall (ORS)',
    }[sensorProduct as string] || 'Rainfall'

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_Rainfall`,
    title: {
      text: title,
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    willReadFrequently: true,
    toolTip: {
      shared: true,

      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          if (e.entries[i].dataSeries.name.includes('(hidden)')) continue
          const name = e.entries[i].dataSeries.name

          const precision =
            name === 'Rate' ? settings.rainfallRate.userSettings.precision : settings.rainfall.userSettings.precision
          const value = e.entries[i].dataPoint.y
          const color = e.entries[i].dataSeries.color
          const itemUnits = e.entries[i].dataSeries.name === 'Rate' ? units.rainfallRate : units.rainfall
          content += `<span style="color:${color}">${name}:</span> <strong> ${
            value !== undefined ? formatNumber(value, precision) : ''
          } ${value !== undefined ? itemUnits : ''}</strong><br/>`
        }

        xRef.current = e.entries[0].dataPoint.x

        return content
      },
    },
    axisX: {
      labelFormatter: createAxisLabelFormatter(),
      viewportMinimum: minX || null,
      viewportMaximum: maxX || null,

      stripLines: [
        ...selectXRange.map((value) => {
          return {
            value,
            color: 'red',
            labelPlacement: 'outside',
          }
        }),

        selectXRange.length === 2 && {
          startValue: selectXRange[0],
          endValue: selectXRange[1],
          color: '#d8d8d8',
        },

        ...hiddenStripLines,
        ...alertMarkersToStripLines(alertMarkers),
      ],
    },
    axisY: [
      {
        includeZero: false,
        title: `Rainfall (${units.rainfall})`,
        minimum: 0,
        maximum: maxYAxis * 1.1,
        titleFontSize: TITLE_FONT_SIZE,
        labelFontSize: LABEL_FONT_SIZE,
      },
    ],
    axisY2: [
      {
        includeZero: false,
        title: `Rainfall Rate (${units.rainfallRate})`,
        minimum: 0,
        maximum: maxRainfallRate * 1.1,
        titleFontSize: TITLE_FONT_SIZE,
        labelFontSize: LABEL_FONT_SIZE,
      },
    ],
    data: [
      ...(showHiddenData && ghostDataPoints.rainfall.length > 0
        ? [
            {
              name: 'Amount (hidden)',
              axisYType: 'primary',
              type: 'stepArea',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              fillOpacity: 0.15,
              markerSize: 0,
              dataPoints: ghostDataPoints.rainfall,
              toolTipContent: null,
            },
            {
              name: 'Total (hidden)',
              axisYType: 'primary',
              type: 'area',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              fillOpacity: 0.15,
              markerSize: 0,
              dataPoints: ghostDataPoints.rainfallTotals,
              toolTipContent: null,
            },
            {
              name: 'Rate (hidden)',
              axisYType: 'secondary',
              type: 'line',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              lineThickness: 2,
              markerSize: 0,
              dataPoints: ghostDataPoints.rainfallRate,
              toolTipContent: null,
            },
          ]
        : []),
      rainfall,
      rainfallTotals,
      rainfallRate,
    ],
    legend: {
      itemclick: function (e: any) {
        switch (e.dataSeries.name) {
          case 'Amount':
            setShowAmounts(!showAmounts)
            break
          case 'Rate':
            setShowRate(!showRate)
            break
          case 'Total':
            setShowTotals(!showTotals)
            break
        }
      },
    },
  }

  function getSubHeaderData() {
    if (!hasReadings || dataPoints.rainfall.length === 0) {
      return [[`Port ${sensorPort}: Rainfall`, 'No readings', '']]
    }

    const index = getLastReadingIndex()
    if (index < 0) {
      return [[`Port ${sensorPort}: Rainfall`, 'No readings', '']]
    }

    const ts = getLastReadingTimestamp()
    const data = [
      [
        `Port ${sensorPort}: Rainfall`,
        `${new Date(ts).toLocaleString()}`,
        `Rate: ${formatNumber(roundToDecimal(dataPoints.rainfallRate[index]?.y ?? 0, precision.rainfallRate))} ${units.rainfallRate}`,
        `Amount: ${formatNumber(roundToDecimal(dataPoints.rainfall[index]?.y ?? 0, precision.rainfall))} ${units.rainfall}`,
        `Total: ${formatNumber(roundToDecimal(dataPoints.rainfallTotals[index]?.y ?? 0, precision.rainfallRate))} ${units.rainfall}`,
      ],
    ]

    return data
  }

  function getLastReadingTimestamp(): number {
    const timestamps = dataPoints.rainfall.map((values: any) => values.x)
    return Number(timestamps[timestamps.length - 1])
  }

  function getLastReadingIndex() {
    const timestamps = dataPoints.rainfall.map((values: any) => values.x)
    return timestamps.length - 1
  }

  // Only render chart if we actually have data points to display
  const hasDataToRender = hasReadings && dataPoints.rainfall.length > 0

  return (
    <CollapsibleSection title="Rainfall" isLoading={isLoading}>
      <ChartSubHeader data={getSubHeaderData()} />
      {isLoading ? (
        <ChartLoadingPlaceholder />
      ) : hasDataToRender ? (
        <div ref={containerRef}>
          <CanvasJSChart options={options} />
        </div>
      ) : (
        <div className="flex items-center justify-center py-8">
          <div className="bg-red-500 text-white font-bold rounded px-6 py-3 text-center">
            This RIC has no data for the selected period
          </div>
        </div>
      )}
    </CollapsibleSection>
  )
}
